import { useRef, useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Eraser } from "lucide-react";

export default function SignaturePad({ onChange, height = 180, testid = "signature-pad" }) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastRef = useRef({ x: 0, y: 0 });
  const [hasInk, setHasInk] = useState(false);

  const setupCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const w = canvas.clientWidth || canvas.parentElement?.clientWidth || 320;
    if (w === 0) return; // wait for layout
    const ratio = window.devicePixelRatio || 1;
    // preserve drawing if any: read pixels first
    const prev = canvas.width > 0 ? canvas.toDataURL("image/png") : null;
    canvas.width = w * ratio;
    canvas.height = height * ratio;
    const ctx = canvas.getContext("2d");
    ctx.setTransform(1, 0, 0, 1, 0, 0); // reset
    ctx.scale(ratio, ratio);
    ctx.strokeStyle = "#1e3a8a";
    ctx.lineWidth = 2.8;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    if (prev && hasInk) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, w, height);
      img.src = prev;
    }
  }, [height, hasInk]);

  useEffect(() => {
    setupCanvas();
    const onResize = () => setupCanvas();
    window.addEventListener("resize", onResize);
    // Re-run a couple of frames later — handles dialog opening animation
    const t1 = setTimeout(setupCanvas, 50);
    const t2 = setTimeout(setupCanvas, 200);
    let ro;
    if (typeof ResizeObserver !== "undefined" && canvasRef.current?.parentElement) {
      ro = new ResizeObserver(() => setupCanvas());
      ro.observe(canvasRef.current.parentElement);
    }
    return () => {
      window.removeEventListener("resize", onResize);
      clearTimeout(t1); clearTimeout(t2);
      if (ro) ro.disconnect();
    };
  }, [setupCanvas]);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const t = (e.touches && e.touches[0]) || (e.changedTouches && e.changedTouches[0]) || e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };

  const emit = () => {
    if (!canvasRef.current) return;
    try {
      const data = canvasRef.current.toDataURL("image/png");
      if (onChange) onChange(data);
    } catch { /* ignore */ }
  };

  const start = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    lastRef.current = pos(e);
    // draw a dot so a single tap registers
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.arc(lastRef.current.x, lastRef.current.y, 1.2, 0, Math.PI * 2);
    ctx.fillStyle = "#1e3a8a";
    ctx.fill();
    if (!hasInk) setHasInk(true);
  };
  const move = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const p = pos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(lastRef.current.x, lastRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastRef.current = p;
    if (!hasInk) setHasInk(true);
  };
  const end = (e) => {
    if (!drawingRef.current) return;
    if (e && e.preventDefault) try { e.preventDefault(); } catch {}
    drawingRef.current = false;
    emit();
  };

  const clear = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasInk(false);
    if (onChange) onChange(null);
  };

  return (
    <div className="space-y-2">
      <div className="relative rounded-xl border-2 border-dashed border-border bg-card overflow-hidden select-none" style={{ touchAction: "none" }}>
        <canvas
          ref={canvasRef}
          style={{ width: "100%", height: `${height}px`, touchAction: "none", display: "block", cursor: "crosshair" }}
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
          onTouchCancel={end}
          data-testid={testid}
        />
        {!hasInk && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none text-muted-foreground text-sm font-medium">
            ✍ Sign here
          </div>
        )}
      </div>
      <div className="flex items-center justify-between">
        <span className="text-[10px] text-muted-foreground">
          {hasInk ? "Signature captured ✓" : "Use your finger or mouse to sign above."}
        </span>
        <Button type="button" variant="ghost" size="sm" className="rounded-xl text-xs h-7" onClick={clear} data-testid={`${testid}-clear`}>
          <Eraser className="w-3 h-3 mr-1" /> Clear
        </Button>
      </div>
    </div>
  );
}
